import { useEffect, useRef, useState } from "react";
import { Share2, Copy, Check, X, Facebook, MessageCircle, Mail } from "lucide-react";
import { toast } from "sonner";

/**
 * X logo (formerly Twitter) — inline SVG so we don't need a new dep.
 */
function XIcon({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M18.244 2H21l-6.522 7.454L22 22h-6.83l-4.75-6.222L4.9 22H2.13l6.982-7.982L2 2h6.998l4.284 5.664L18.244 2Zm-2.395 18.5h1.878L7.246 3.396H5.246L15.849 20.5Z" />
    </svg>
  );
}

const COLORS = {
  x: "hover:bg-[#000] hover:text-white",
  facebook: "hover:bg-[#1877f2] hover:text-white",
  whatsapp: "hover:bg-[#25d366] hover:text-white",
  sms: "hover:bg-[#4ecdc4] hover:text-white",
  email: "hover:bg-[#f7c873]",
  copy: "hover:bg-[#f7c873]",
};

export default function ShareMenu({ coupon, testid }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const ref = useRef(null);
  const btnRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (
        ref.current &&
        !ref.current.contains(e.target) &&
        btnRef.current &&
        !btnRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    const onEsc = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const shareUrl = `${typeof window !== "undefined" ? window.location.origin : ""}/coupon/${coupon.id}`;
  const shareText = coupon.code
    ? `${coupon.discount_label} at ${coupon.store} — use code ${coupon.code} on The Couponer`
    : `${coupon.discount_label} at ${coupon.store} — snag it on The Couponer`;

  const share = (channel) => {
    const url = encodeURIComponent(shareUrl);
    const text = encodeURIComponent(shareText);
    const targets = {
      x: `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      whatsapp: `https://wa.me/?text=${text}%20${url}`,
      sms: `sms:?body=${text}%20${url}`,
      email: `mailto:?subject=${encodeURIComponent(coupon.store + " deal on The Couponer")}&body=${text}%20${url}`,
    };
    if (targets[channel]) {
      const isSelf = channel === "sms" || channel === "email";
      window.open(targets[channel], isSelf ? "_self" : "_blank", "noopener");
      setOpen(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopied(false), 1800);
      setOpen(false);
    } catch {
      toast.error("Copy blocked by browser");
    }
  };

  const Item = ({ label, onClick, Icon, colorKey, testid }) => (
    <button
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      data-testid={testid}
      className={`flex items-center gap-3 w-full px-3 py-2 rounded-md text-left text-sm font-semibold text-[#2d2a32] transition-colors ${COLORS[colorKey] || ""}`}
    >
      <span className="w-8 h-8 rounded-full border-2 border-[#2d2a32] flex items-center justify-center bg-white">
        <Icon className="w-4 h-4" />
      </span>
      {label}
    </button>
  );

  return (
    <div className="relative inline-block">
      <button
        ref={btnRef}
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        data-testid={testid || `share-btn-${coupon.id}`}
        aria-label="Share coupon"
        aria-expanded={open}
        className="w-10 h-10 border-2 border-[#2d2a32] rounded-full flex items-center justify-center bg-white hover:bg-[#4ecdc4] hover:text-white"
      >
        <Share2 className="w-4 h-4" />
      </button>

      {open && (
        <div
          ref={ref}
          data-testid={`share-menu-${coupon.id}`}
          className="absolute z-40 top-12 right-0 w-64 border-2 border-[#2d2a32] rounded-xl bg-white hard-shadow-sm p-2"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between px-2 pt-1 pb-2">
            <span className="mono text-[10px] tracking-[0.25em] text-[#5c5c5c]">SHARE THIS DEAL</span>
            <button
              onClick={() => setOpen(false)}
              className="w-6 h-6 rounded-full hover:bg-[#fff2d6] flex items-center justify-center"
              aria-label="Close share menu"
              data-testid={`share-close-${coupon.id}`}
            >
              <X className="w-3 h-3" />
            </button>
          </div>
          <div className="flex flex-col gap-1">
            <Item label="X (Twitter)" onClick={() => share("x")} Icon={XIcon} colorKey="x" testid={`share-x-${coupon.id}`} />
            <Item label="Facebook" onClick={() => share("facebook")} Icon={Facebook} colorKey="facebook" testid={`share-facebook-${coupon.id}`} />
            <Item label="WhatsApp" onClick={() => share("whatsapp")} Icon={MessageCircle} colorKey="whatsapp" testid={`share-whatsapp-${coupon.id}`} />
            <Item label="Text (SMS)" onClick={() => share("sms")} Icon={MessageCircle} colorKey="sms" testid={`share-sms-${coupon.id}`} />
            <Item label="Email" onClick={() => share("email")} Icon={Mail} colorKey="email" testid={`share-email-${coupon.id}`} />
            <div className="my-1 border-t border-dashed border-[#2d2a32]/30" />
            <Item
              label={copied ? "Link copied!" : "Copy link"}
              onClick={copyLink}
              Icon={copied ? Check : Copy}
              colorKey="copy"
              testid={`share-copy-${coupon.id}`}
            />
          </div>
        </div>
      )}
    </div>
  );
}
